import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { differenceInBusinessDays, parseISO, isWithinInterval, isFuture, format } from 'date-fns';
import { db } from '../../firebase';
import { collection, getDocs, query, where, setDoc, doc } from 'firebase/firestore';
import { Item } from '../dashboardItems/eDashboard';

const AnnualLeave = ({ user }) => {
  const [annualExhaustedDays, setAnnualExhaustedDays] = useState(0);
  const [annualOffHours, setAnnualOffHours] = useState(0);
  const [remainingAnnualLeaves, setRemainingAnnualLeaves] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [annualPeriod, setAnnualPeriod] = useState({ start: '', end: '' });
  const [upcomingLeave, setUpcomingLeave] = useState('No leave planned');

  const fetchAnnualLeaveData = useCallback(async () => {
    if (!user || !user.joiningDate || !user.allowedLeave) {
      console.error("User data is incomplete.");
      return;
    }
  
    const joiningDate = parseISO(user.joiningDate);
    const startOfYear = new Date(
      new Date().getFullYear(),
      joiningDate.getMonth(),
      joiningDate.getDate()
    );
    const endOfYear = new Date(
      startOfYear.getFullYear() + 1,
      joiningDate.getMonth(),
      joiningDate.getDate() - 1
    );
  
    setAnnualPeriod({
      start: format(startOfYear, "dd MMMM yyyy"),
      end: format(endOfYear, "dd MMMM yyyy"),
    });
 
    const docId = `${user.id}-${format(startOfYear, 'yyyy-MM-dd')}_${format(endOfYear, 'yyyy-MM-dd')}`;
  
    try {
      // Fetch all leaves for the employee
      const q = query(
        collection(db, "employeeLeave"),
        where("employeeId", "==", user.id)
      );
      const querySnapshot = await getDocs(q);
      const leaves = querySnapshot.docs.map((doc) => doc.data());
  
      // Filter and process annual leave
      const annualLeaves = leaves.filter((leave) =>
        isWithinInterval(parseISO(leave.startDate), {
          start: startOfYear,
          end: endOfYear,
        })
      );
  
      const approvedAnnualLeaves = annualLeaves.filter(
        (leave) => leave.status === "Approved"
      );
  
      const pendingLeaves = annualLeaves.filter(
        (leave) => leave.status === "Pending"
      );
  
      // Find the next upcoming approved leave
      const futureLeave = approvedAnnualLeaves.find((leave) =>
        isFuture(parseISO(leave.startDate))
      );
  
      setUpcomingLeave(
        futureLeave
          ? `${format(parseISO(futureLeave.startDate), "dd MMM yyyy")} to ${format(
              parseISO(futureLeave.endDate),
              "dd MMM yyyy"
            )}`
          : "No leave planned"
      );
  
      // Calculate metrics
      const exhaustedDays = approvedAnnualLeaves.reduce((sum, leave) => {
        const days = differenceInBusinessDays(
          parseISO(leave.endDate),
          parseISO(leave.startDate)
        );
        return sum + days + 1; // Include the start day
      }, 0);
  
      const totalOffHours = exhaustedDays * 9; // Assuming 9 hours/day
      const remainingLeaves = Math.max(0, user.allowedLeave - exhaustedDays);
  
      // Update state
      setAnnualExhaustedDays(exhaustedDays);
      setAnnualOffHours(totalOffHours);
      setRemainingAnnualLeaves(remainingLeaves);
      setPendingApprovalsCount(pendingLeaves.length);
  
      // Save data to Firestore
      const metricsDocRef = doc(db, "employeeAnnualLeave", docId);
      await setDoc(metricsDocRef, {
        employeeId: user.id,
        annualExhaustedDays: exhaustedDays,
        annualOffHours: totalOffHours,
        remainingAnnualLeaves: remainingLeaves,
        pendingApprovalsCount: pendingLeaves.length,
        annualPeriod: {
          start: format(startOfYear, "dd MMMM yyyy"),
          end: format(endOfYear, "dd MMMM yyyy"),
        },
        upcomingLeave: futureLeave
          ? `${format(parseISO(futureLeave.startDate), "dd MMM yyyy")} to ${format(
              parseISO(futureLeave.endDate),
              "dd MMM yyyy"
            )}`
          : "No leave planned",
        timestamp: new Date().toISOString(),
      });
  
      console.log("Annual leave metrics saved successfully.");
    } catch (error) {
      console.error("Error fetching or saving annual leave data:", error);
    }
  }, [user]);  

  useEffect(() => {
    fetchAnnualLeaveData();
  }, [fetchAnnualLeaveData]);

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left Column: Annual Period and Upcoming Leave */}
        <Grid item xs={12} md={4}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Annual Period
                </Typography>
                <Typography>
                  {annualPeriod.start} - {annualPeriod.end}
                </Typography>
              </Item>
            </Grid>
            <Grid item>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Upcoming Leave
                </Typography>
                <Typography>{upcomingLeave}</Typography>
              </Item>
            </Grid>
          </Grid>
        </Grid>
  
        {/* Right Column: Other Items */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {/* First Row */}
            <Grid item xs={12} sm={6}>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Exhausted Leaves
                </Typography>
                <Typography variant="h4" sx={{ color: '#39B54A' }}>
                  {annualExhaustedDays}
                </Typography>
                <Typography variant="body2">Days</Typography>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Total Off Hours
                </Typography>
                <Typography variant="h4" sx={{ color: '#39B54A' }}>
                  {annualOffHours}
                </Typography>
                <Typography variant="body2">Hours</Typography>
              </Item>
            </Grid>
            {/* Second Row */}
            <Grid item xs={12} sm={6}>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Remaining Leaves
                </Typography>
                <Typography variant="h4" sx={{ color: '#39B54A' }}>
                  {remainingAnnualLeaves}
                </Typography>
                <Typography variant="body2">Days</Typography>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item sx={{ padding: 2, height: 120, position: 'relative' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Pending Approvals
                </Typography>
                <Typography variant="h4" sx={{ color: '#39B54A' }}>
                  {pendingApprovalsCount}
                </Typography>
                <Typography variant="body2">Requests</Typography>
              </Item>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnnualLeave;
